import type { Point2d } from "@logic/lib/2d"
import { foreshortenScaleIsomorphic } from "@logic/lib/2d"
import { MAX_AUTO_PLACED_PANELS } from "@logic/lib/constants"
import type { Panel } from "@logic/lib/design"
import { placePanels } from "@logic/lib/design"
import type { Face2dComputed } from "@logic/lib/mesh"
import { makeInchScaler, Index1ToMany } from "@logic/lib/utils"
import { useRecoilTransaction_UNSTABLE, atom } from "recoil"

import { showAutoPlacePanelState } from "~/containers/LegacyDesigner/SidePanel/AutoPlacePanelButton"
import { designLocalState } from "~/containers/Proposal/state/designs/design-state"
import { siteLocalState } from "~/containers/Proposal/state/site/site-state"

import type { TransactionOperation } from "."
import { getPanelFootprint } from "./getPanelFootprint"
import { getPreferredImage } from "./getPreferredImage"
import { areaIndex, findAreaState } from "../areas"
import { findPanelState, panelGapState, panelIndex } from "../panels"
import { areasOfPanelsIndexState } from "../relations"
import type { UnknownChangeForUnknownElement } from "../timeline"
import { divergeTimeline, generateElementChange } from "../timeline/transactions"

export const panelAutoPlacementNodesState = atom<Point2d[][]>({
  key: `panelAutoPlacementNodes`,
  default: [],
})

export const panelAutoPlacementFacesState = atom<Face2dComputed[]>({
  key: `panelAutoPlacementFaces`,
  default: [],
})

export const getInchScaler: TransactionOperation<
  undefined,
  (inches: number) => number
> = (transactors) => {
  const { get } = transactors
  const { latitude } = get(siteLocalState)
  const { zoomLevel } = getPreferredImage(transactors)

  return makeInchScaler({ latitude, zoomLevel })
}

export const autoPlacePanels: TransactionOperation = (transactors) => {
  const { get, set } = transactors

  const changes: UnknownChangeForUnknownElement[] = []
  const panelIds: string[] = []
  const areaPanels = new Index1ToMany<string, string>()

  const footprint = getPanelFootprint(transactors, null)
  if (footprint.h === 0 || footprint.w === 0) {
    throw new Error(`Module scale is 0`)
  }
  const areaIds = get(areaIndex)
  const design = get(designLocalState)
  const inchesToPixels = getInchScaler(transactors)
  let numberOfPanels = 0
  for (const areaId of areaIds) {
    const { slope } = get(findAreaState(areaId))
    const moduleScaleInPerspective = foreshortenScaleIsomorphic({
      h: footprint.h,
      w: footprint.w,
      degreesTiltedFromSurface: slope,
      axis: `y`,
    })
    const { panels, procedure } = placePanels({
      design,
      areaId,
      moduleScale: moduleScaleInPerspective,
      inchesToPixels,
      numberOfPanels,
    })

    numberOfPanels += Object.entries(panels).length

    set(panelAutoPlacementNodesState, procedure.nodes)
    set(panelAutoPlacementFacesState, procedure.faces)
    for (const panelTuple of Object.entries(panels)) {
      const [panelId, panel] = panelTuple as [string, Panel]
      set(findPanelState(panelId), panel)
      panelIds.push(panelId)
      areaPanels.set(areaId, panelId)
      const panelCreation = generateElementChange(transactors, {
        action: {
          type: `CREATE`,
          family: `PANEL`,
          id: panelId,
        },
      })
      const panelAttachment = generateElementChange(transactors, {
        action: {
          type: `ATTACH`,
          family: `AREA_PANEL`,
          id: areaId,
          relatedId: panelId,
        },
      })
      changes.push(panelCreation, panelAttachment)
    }

    if (numberOfPanels >= MAX_AUTO_PLACED_PANELS) {
      break
    }
  }

  set(panelIndex, panelIds)
  set(areasOfPanelsIndexState, areaPanels)
  set(showAutoPlacePanelState, true)
  divergeTimeline(transactors, changes)
}

export const useAutoPlacePanels = (): VoidFunction =>
  useRecoilTransaction_UNSTABLE(
    (transactors) => () => autoPlacePanels(transactors),
  )
